import React from 'react';
import { Copy, Phone, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import type { VoucherRedemptionGuide } from './voucherRedemptionGuides';
import { buildVoucherUssdDialString } from './voucherRedemptionGuides';

interface VoucherRedemptionInstructionsProps {
  guide: VoucherRedemptionGuide;
  className?: string;
  /** When set with ussdDialPattern, shows and copies the full dial string including PIN */
  voucherPin?: string;
}

export function VoucherRedemptionInstructions({
  guide,
  className = '',
  voucherPin,
}: VoucherRedemptionInstructionsProps) {
  const [copiedUssd, setCopiedUssd] = React.useState(false);
  const [copiedHelpline, setCopiedHelpline] = React.useState(false);

  const dialPattern = guide.ussdDialPattern;
  const displayUssdCode = dialPattern
    ? `${dialPattern.prefix}${(voucherPin ? voucherPin.replace(/\D/g, '') : dialPattern.examplePin)}${dialPattern.suffix}`
    : guide.ussdCode;
  const ussdCopyValue = dialPattern && voucherPin
    ? buildVoucherUssdDialString(guide, voucherPin) || displayUssdCode
    : displayUssdCode;

  const copyText = async (value: string, setCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div
      className={className}
      style={{
        textAlign: 'left',
        borderRadius: '12px',
        border: '1px solid #dbeafe',
        backgroundColor: '#f8fafe',
        padding: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: '#e0f2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <Smartphone style={{ width: '18px', height: '18px', color: '#2D8CCA' }} />
        </div>
        <div>
          <h4
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1f2937',
              margin: 0,
              marginBottom: '4px',
            }}
          >
            {guide.title}
          </h4>
          <p
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              color: '#4b5563',
              margin: 0,
              lineHeight: 1.45,
            }}
          >
            {guide.summary}
          </p>
        </div>
      </div>

      {guide.showRecommendedUssd !== false && (
      <div
        style={{
          backgroundColor: '#ffffff',
          border: '2px solid #2D8CCA',
          borderRadius: '12px',
          padding: '0.875rem',
          marginBottom: '0.875rem',
        }}
      >
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#2D8CCA',
            margin: '0 0 6px 0',
          }}
        >
          Recommended — USSD
        </p>
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '12px',
            color: '#6b7280',
            margin: '0 0 8px 0',
          }}
        >
          {guide.ussdLabel}
        </p>
        <p
          style={{
            fontFamily: 'Monaco, Courier, monospace',
            fontSize: dialPattern ? '16px' : '22px',
            fontWeight: 700,
            color: '#1f2937',
            letterSpacing: '1px',
            margin: '0 0 10px 0',
            wordBreak: 'break-all',
            lineHeight: 1.35,
          }}
        >
          {displayUssdCode}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => copyText(ussdCopyValue, setCopiedUssd)}
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '13px',
            fontWeight: 500,
            minHeight: '40px',
            borderRadius: '10px',
            border: '2px solid #2D8CCA',
            color: '#2D8CCA',
            backgroundColor: '#ffffff',
            width: '100%',
          }}
        >
          <Copy style={{ width: '15px', height: '15px', marginRight: '8px' }} />
          {copiedUssd ? 'USSD code copied' : dialPattern ? 'Copy dial string' : 'Copy USSD code'}
        </Button>
      </div>
      )}

      <ol
        style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '12px',
          color: '#374151',
          lineHeight: 1.5,
          margin: '0 0 0.875rem 0',
          paddingLeft: '1.15rem',
        }}
      >
        {guide.steps.map((step) => (
          <li key={step} style={{ marginBottom: '6px' }}>
            {step}
          </li>
        ))}
      </ol>

      <div
        style={{
          borderTop: '1px solid #e5e7eb',
          paddingTop: '0.75rem',
          marginBottom: '0.75rem',
        }}
      >
        <p
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '11px',
            fontWeight: 600,
            color: '#6b7280',
            margin: '0 0 8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Other ways to register
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {guide.alternatives.map((item) => (
            item.href ? (
              <a
                key={item.label}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#2D8CCA',
                  textDecoration: 'none',
                }}
              >
                {item.label}
              </a>
            ) : (
              <span
                key={item.label}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#374151',
                }}
              >
                {item.label}
              </span>
            )
          ))}
        </div>
      </div>

      {guide.helpline && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexWrap: 'wrap',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            padding: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Phone style={{ width: '16px', height: '16px', color: '#6b7280' }} aria-hidden="true" />
            <div>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '11px',
                  color: '#6b7280',
                  margin: 0,
                }}
              >
                {guide.helpline.label}
              </p>
              <a
                href={`tel:${guide.helpline.tel}`}
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1f2937',
                  textDecoration: 'none',
                }}
              >
                {guide.helpline.display}
              </a>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => copyText(guide.helpline!.tel, setCopiedHelpline)}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              borderRadius: '8px',
              minHeight: '36px',
            }}
          >
            {copiedHelpline ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}
    </div>
  );
}
